// External
/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { string, elementType, bool } from 'prop-types';
import cn from 'classnames';

// Internal
import normalize from '../util/normalize';
import ToolTip from './ToolTip';

// Constants
const classname = 'truncator';
const { debounce } = normalize;

/**
 * @name Truncator
 * @description add ellipses when overflow
 * @param {string} [className] append class name
 * @param {string} value string to use as tooltip | title
 * @param {bool} [withToolTip=false] disable toolTip
 * @param {positions} [toolTipPosition=top] tooltip position
 * @param {bool}  [enableRecalc=false] enable check for adding ellipses on resize
 * @example
 * <Truncator value={'my title'}/>
 * @example
 * <Truncator value={'helpful info'} enableRecalc>
 *  <div>some content</div>
 * </Truncator>
 */
const Truncator = ({ withToolTip, className, children, value = '', enableRecalc, toolTipPosition }) => {
  // Instance
  const textElementRef = useRef();
  const derivedClassname = cn(className, classname);
  const styledValue = value.toUpperCase();

  // State
  const [withEllipses, setUseEllipses] = useState(true);

  // Funcs
  const compareSize = () => {
    const compare = textElementRef?.current?.scrollWidth > textElementRef?.current?.clientWidth;
    setUseEllipses(compare);
  };

  // Lifecycles
  useEffect(() => {
    if (enableRecalc) {
      compareSize();
      const debouncedCompare = debounce(compareSize, 500);
      window.addEventListener('resize', debouncedCompare);
      return () => {
        window.removeEventListener('resize', debouncedCompare);
      };
    }
    return () => {};
  }, []);

  if (withToolTip) {
    if (!withEllipses) {
      const noHoverClassname = cn('noHover', derivedClassname);
      return (
        <div className={noHoverClassname} title={styledValue} ref={textElementRef}>
          {children || value}
        </div>
      );
    }
    return (
      <div className={derivedClassname}>
        <ToolTip title={`${styledValue}`} position={toolTipPosition}>
          <div className={`${derivedClassname}__contentWrapper`} ref={textElementRef}>
            {children || value}
          </div>
        </ToolTip>
      </div>
    );
  }

  return (
    <div className={derivedClassname}>
      <div title={`${styledValue}`} className={`${derivedClassname}__contentWrapper`} ref={textElementRef}>
        {children || value}
      </div>
    </div>
  );
};

Truncator.defaultProps = {
  children: null,
  className: '',
  enableRecalc: false,
  toolTipPosition: 'top',
  withToolTip: false
};

Truncator.propTypes = {
  value: string.isRequired,
  toolTipPosition: string,
  children: elementType,
  className: string,
  enableRecalc: bool,
  withToolTip: bool
};

export default Truncator;
